import { LocaleType } from "../../constants/constants";

const FRESHDESK_WIDGET_ID = 206000000735;
const FRESHDESK_WIDGET_SCRIPT_ID = "freshdesk-widget-script";
const FRESHDESK_WIDGET_SCRIPT_SRC = `https://euc-widget.freshworks.com/widgets/${FRESHDESK_WIDGET_ID}.js`;

interface IFreshdeskFieldOverrideOptions {
    fieldValue: string | null | undefined;
    hideField: boolean;
}

class FreshdeskWidgetService {
    private overriddenFields: Map<string, IFreshdeskFieldOverrideOptions> = new Map();

    public initializeWidget(locale: LocaleType) {
        this.destroyWidget();
        const freshdeskLocale = this.mapLocaleTypeToFreshdeskLocale(locale);

        window.fwSettings = {
            widget_id: FRESHDESK_WIDGET_ID,
            locale: freshdeskLocale,
        };

        this.loadWidgetScript();
        this.installWidgetStub();
        window.FreshworksWidget("hide", "launcher");
        this.fillOverriddenFields();
    }

    private destroyWidget() {
        this.withActiveWidget(widget => widget("destroy"));

        this.removeWidgetScript();
        this.resetWidgetStub();
        delete window.fwSettings;
    }

    public showWidget() {
        this.withActiveWidget(widget => widget("show"));
    }

    public hideWidget() {
        this.withActiveWidget(widget => widget("hide"));
    }

    public openWidget() {
        this.withActiveWidget(widget => widget("open"));
    }

    public setUserName(name: string) {
        this.setWidgetFieldOverriddenValue("name", {fieldValue: name, hideField: true});
    }

    public setUserEmail(email: string) {
        this.setWidgetFieldOverriddenValue("email", {fieldValue: email, hideField: false});
    }

    public setUserWarehouse(warehouseCode: string | undefined) {
        this.setWidgetFieldOverriddenValue("cf_lni_warehouse_code", {fieldValue: warehouseCode, hideField: true});
    }

    public setOperationCodes(operationCodes: string[]) {
        const operationCodesString = operationCodes.join(", ");
        this.setWidgetFieldOverriddenValue("cf_operation_codes", {fieldValue: operationCodesString, hideField: true});
    }

    private loadWidgetScript() {
        this.removeWidgetScript();

        const script = document.createElement("script");
        script.id = FRESHDESK_WIDGET_SCRIPT_ID;
        script.type = "text/javascript";
        script.src = FRESHDESK_WIDGET_SCRIPT_SRC;
        script.async = true;
        script.defer = true;

        document.body.appendChild(script);
    }

    private removeWidgetScript() {
        const existingScript = document.getElementById(FRESHDESK_WIDGET_SCRIPT_ID);

        if (existingScript && existingScript.parentNode) {
            existingScript.parentNode.removeChild(existingScript);
        }
    }

    private installWidgetStub() {
        if (typeof window.FreshworksWidget === "function") {
            return;
        }

        const stub = ((...args: unknown[]) => {
            stub.q.push(args);
        }) as FreshworksWidgetApi;

        stub.q = [];
        window.FreshworksWidget = stub;
    }

    private resetWidgetStub() {
        const stub = ((...args: unknown[]) => {
            stub.q.push(args);
        }) as FreshworksWidgetApi;

        stub.q = [];
        window.FreshworksWidget = stub;
    }

    private fillOverriddenFields() {
        for (const [fieldName, fieldOptions] of this.overriddenFields) {
            this.updateWidgetFieldValue(fieldName, fieldOptions);
        }
    }

    private setWidgetFieldOverriddenValue(fieldName: string, fieldOptions: IFreshdeskFieldOverrideOptions) {
        this.overriddenFields.set(fieldName, fieldOptions);
        this.updateWidgetFieldValue(fieldName, fieldOptions);
    }

    private updateWidgetFieldValue(fieldName: string, fieldOptions: IFreshdeskFieldOverrideOptions) {
        if (!fieldOptions.fieldValue) {
            return;
        }

        const isCustomField = fieldName.startsWith("cf_");

        this.withActiveWidget(widget => {
            widget("identify", "ticketForm", {
                [`${isCustomField ? "customFields." : ""}${fieldName}`]: fieldOptions.fieldValue,
            });

            if (fieldOptions.hideField) {
                widget("hide", "ticketForm", [`${isCustomField ? "custom_fields." : ""}${fieldName}`]);
            }
        });
    }

    private withActiveWidget(action: (widget: FreshworksWidgetApi) => void) {
        if (typeof window.FreshworksWidget !== "function") {
            return;
        }

        action(window.FreshworksWidget);
    }

    private mapLocaleTypeToFreshdeskLocale(locale: LocaleType) {
        switch (locale) {
            case "en-GB":
                return "en";
            case "fr-FR":
                return "fr";
            case "es-ES":
                return "es";
            case "it-IT":
                return "it";
            default:
                return "en";
        }
    }
}

export const freshdeskWidgetService = new FreshdeskWidgetService();
